/* Master Order → Расчёты → Пересчитать рецепт.
 * Recognition is client-side; the existing Workbook flow persists results.
 * 4 phases: paste → review → resize → result.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Circle, Square, RectangleHorizontal, Copy, Plus, X } from "lucide-react";
import MOShell from "@/components/masterOrder/MOShell";
import RecipeCostDrawer from "@/components/masterOrder/RecipeCostDrawer";
import { createWorkbookEntry, updateWorkbookEntry, duplicateWorkbookEntry, listWorkbookEntries, getWorkbookEntry } from "@/lib/workbookApi";
import { listIngredients, createIngredient, createRecipe, unitLabel, displayOrderNumber } from "@/lib/masterOrderApi";
import { trackMasterOrderTool } from "@/lib/masterOrderAnalytics";
import { hasRecognizedIngredients, ingredientReviewWarning, parseRecipe } from "./recipeRecognition.mjs";
import { RECIPE_CATEGORIES, categoryForRecipeSave, recipeCategoryOrFallback, suggestRecipeCategory } from "./recipeCategories.mjs";
import {
  MEASURE_STATE,
  applyMetricResolution,
  formatMeasureNumber,
  getIngredientMeasureState,
} from "./measureDirectory.mjs";

export { parseRecipe } from "./recipeRecognition.mjs";

const MY_RECIPE_BASE_UNIT = Object.freeze({
  g: "g", kg: "g",
  ml: "ml", l: "ml",
  pcs: "pcs",
});
const MY_RECIPE_UNIT_FAMILY = Object.freeze({
  g: "mass", kg: "mass",
  ml: "volume", l: "volume",
  pcs: "count",
});
const isEggIngredient = (name) => /(?:яйц|яиц|белок|белки|желток|желтк)/i.test(String(name || ""));
const isEggMassCountPair = (name, recipeUnit, baseUnit) =>
  isEggIngredient(name) && new Set([recipeUnit, baseUnit]).size === 2
  && [recipeUnit, baseUnit].every((unit) => unit === "g" || unit === "pcs");

/* Small helper: format the picker card's "Обновлено …" line (DD.MM.YYYY). */
const fmtEntryDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  } catch { return "—"; }
};

/* ─────────── geometry ───────────── */
const shapeArea = (s) => {
  if (s.shape === "round") return Math.PI * (Number(s.diameter) / 2) ** 2;
  if (s.shape === "square") return Number(s.side) ** 2;
  if (s.shape === "rect") return Number(s.length) * Number(s.width);
  return 0;
};
const shapeSummary = (s) => {
  const h = s.height ? ` · h ${s.height} см` : "";
  const n = s.count > 1 ? ` × ${s.count}` : "";
  if (s.shape === "round") return `Круг Ø${s.diameter} см${h}${n}`;
  if (s.shape === "square") return `Квадрат ${s.side}×${s.side} см${h}${n}`;
  return `Прямоугольник ${s.length}×${s.width} см${h}${n}`;
};

const emptyShape = () => ({ shape: "round", diameter: 20, side: 20, length: 25, width: 15, height: "", count: 1 });

/* ─────────── UI primitives ─────────── */
const Card = ({ children, testid }) => (
  <div className="rounded-xl bg-white p-4" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid={testid}>
    {children}
  </div>
);
const Label = ({ children }) => (
  <div className="text-[10.5px] tracking-[0.14em] uppercase mb-1.5" style={{ color: "#8B6F4B" }}>{children}</div>
);
const NumInput = ({ value, onChange, placeholder, testid }) => (
  <input type="text" inputMode="decimal" value={value ?? ""}
         onChange={(e) => onChange(e.target.value.replace(",", "."))}
         placeholder={placeholder}
         className="w-full h-10 px-3 rounded-md bg-white text-base md:text-[13px] outline-none tabular-nums"
         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
         data-testid={testid} />
);
const PrimaryBtn = ({ onClick, disabled, testid, children }) => (
  <button onClick={onClick} disabled={disabled}
          className="h-11 px-5 rounded-xl text-[13px] disabled:opacity-60"
          style={{ background: "#4A3728", color: "#F7EFE0" }}
          data-testid={testid}>{children}</button>
);
const GhostBtn = ({ onClick, testid, children }) => (
  <button onClick={onClick}
          className="h-11 px-4 rounded-xl text-[13px]"
          style={{ background: "transparent", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
          data-testid={testid}>{children}</button>
);

const fitTechnologyTextarea = (node) => {
  if (!node) return;
  node.style.height = "auto";
  const nextHeight = Math.min(420, Math.max(180, node.scrollHeight));
  node.style.height = `${nextHeight}px`;
  node.style.overflowY = node.scrollHeight > 420 ? "auto" : "hidden";
};

function TechnologyReviewTextarea({ value, onChange }) {
  const ref = useRef(null);
  useEffect(() => { fitTechnologyTextarea(ref.current); }, [value]);
  return <textarea ref={ref} value={value || ""}
                   onChange={(event) => onChange(event.target.value.slice(0, 2000))}
                   onInput={(event) => fitTechnologyTextarea(event.currentTarget)}
                   rows={8}
                   placeholder="Заморозить 30 минут, выпекать при 170°C…"
                   className="w-full p-3 rounded-md bg-white text-base md:text-[13px] leading-relaxed outline-none resize-y"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14", minHeight: "180px", maxHeight: "420px", overflowY: "auto" }}
                   data-testid="mo-recalc-technique-text" />;
}

const ShapePicker = ({ value, onChange, testidPrefix }) => (
  <div className="flex gap-1.5" data-testid={`${testidPrefix}-picker`}>
    {[
      { v: "round", label: "Круг", Icon: Circle },
      { v: "square", label: "Квадрат", Icon: Square },
      { v: "rect", label: "Прямоуг.", Icon: RectangleHorizontal },
    ].map(({ v, label, Icon }) => (
      <button key={v} onClick={() => onChange(v)}
              className="h-9 px-3 rounded-xl text-[12px] inline-flex items-center gap-1.5"
              style={{
                background: value === v ? "#4A3728" : "#F7F1E7",
                color: value === v ? "#F7EFE0" : "#4A3728",
                boxShadow: value === v ? "none" : "inset 0 0 0 1px #EFE4D0",
              }}
              data-testid={`${testidPrefix}-${v}`}>
        <Icon size={12} /> {label}
      </button>
    ))}
  </div>
);

const ShapeCard = ({ title, value, setValue, testidPrefix }) => {
  const update = (k, v) => setValue({ ...value, [k]: v });
  return (
    <Card testid={`${testidPrefix}-card`}>
      <div className="text-[13px] mb-2" style={{ color: "#2A1D14" }}>{title}</div>
      <ShapePicker value={value.shape} onChange={(v) => update("shape", v)} testidPrefix={testidPrefix} />
      <div className="mt-3 grid grid-cols-2 gap-2">
        {value.shape === "round" && (
          <div><Label>Ø см</Label><NumInput value={value.diameter} onChange={(v) => update("diameter", v)} testid={`${testidPrefix}-diameter`} /></div>
        )}
        {value.shape === "square" && (
          <div><Label>Сторона см</Label><NumInput value={value.side} onChange={(v) => update("side", v)} testid={`${testidPrefix}-side`} /></div>
        )}
        {value.shape === "rect" && (<>
          <div><Label>Длина см</Label><NumInput value={value.length} onChange={(v) => update("length", v)} testid={`${testidPrefix}-length`} /></div>
          <div><Label>Ширина см</Label><NumInput value={value.width} onChange={(v) => update("width", v)} testid={`${testidPrefix}-width`} /></div>
        </>)}
        <div><Label>Высота см</Label><NumInput value={value.height} onChange={(v) => update("height", v)} placeholder="—" testid={`${testidPrefix}-height`} /></div>
        <div><Label>Форм, шт</Label><NumInput value={value.count} onChange={(v) => update("count", v)} testid={`${testidPrefix}-count`} /></div>
      </div>
    </Card>
  );
};

const fmtNum = (n, digits = 1) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  const rounded = v >= 10 ? Math.round(v) : Number(v.toFixed(digits));
  return String(rounded).replace(".", ",");
};

/* ─── Practical rounding for pastry-chef ingredient amounts ────────────────
 * The math (area/volume ratio) can produce values like 82.7605 g of powdered
 * sugar or 1.2732 g of salt. Those are inconvenient to weigh.
 * We map the exact math result to a working amount whose deviation is
 * within ~3 % for normal ingredients, keeping precise / very-precise
 * ingredients closer to real dosing (0.1 g / 0.01 g resolution).
 *
 * Classification is by NAME (Russian keyword patterns), so callers must pass
 * the ingredient row, not just its number. Piece units are never rescaled —
 * the existing pcs / grams_per_piece logic elsewhere in the app owns that.
 * -------------------------------------------------------------------------- */
const RX_VERY_PRECISE = /(красител|пищев\w* краск|ароматизатор|эссенц|экстракт|эфирн\w* масл|концентрат)/i;
const RX_PRECISE = /(пектин|агар|желатин|разрыхлит|сод[ауыи]\b|\bсол[ьюия]|лимонн\w* кислот|кислот[аыуе]\b|ксантан|гуар|эмульгатор|лецитин|диоксид|дрожж)/i;

const classifyIngredient = (name, unit) => {
  if (unit === "pcs") return "piece";
  const s = String(name || "");
  if (RX_VERY_PRECISE.test(s)) return "very_precise";
  if (RX_PRECISE.test(s)) return "precise";
  return "normal";
};

const NORMAL_STEPS_G = [5, 2, 1, 0.5, 0.1]; // largest → smallest
const roundToStep = (v, step) => Math.round(v / step) * step;
const relErr = (exact, r) => (exact === 0 ? 0 : Math.abs(r - exact) / Math.abs(exact));
const stripFloat = (n, d) => parseFloat(Number(n).toFixed(d));

/* Structural pcs: limited to eggs/whites/yolks. Berries, cookies and decor
 * stay fractional because chef intent varies too much to auto-round them. */

const workingAmountFor = (exactRaw, name, unit) => {
  const exact = Number(exactRaw);
  if (!Number.isFinite(exact)) return exactRaw;
  const cls = classifyIngredient(name, unit);
  if (cls === "piece") return exact; // pcs untouched

  const applyGramsClass = (grams) => {
    if (cls === "normal") {
      for (const step of NORMAL_STEPS_G) {
        const r = roundToStep(grams, step);
        if (r > 0 && relErr(grams, r) <= 0.03) return stripFloat(r, 3);
      }
      return stripFloat(grams, 1);
    }
    if (cls === "precise") return stripFloat(grams, 1);
    // very_precise
    return stripFloat(grams, 2);
  };

  if (unit === "g" || unit === "ml") return applyGramsClass(exact);
  if (unit === "kg" || unit === "l") {
    // Apply the same ladder in grams-scale and convert back so 0.083529 kg → 0.085 kg
    const small = applyGramsClass(exact * 1000);
    return stripFloat(small / 1000, 4);
  }
  // tbsp / tsp / cup / pinch / taste and everything else — 2 decimals, no ladder.
  return stripFloat(exact, 2);
};


/* ─────────── main page ─────────── */
export default function MORecipeRecalc() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Sprint MO-UX-Simplify: when opened from an Order position, we surface
  // a «К заказу №N» chip so the confectioner never loses their order.
  const orderCtxId = searchParams.get("order_id");
  const orderItemCtxId = searchParams.get("position_id");
  const [orderCtxNumber, setOrderCtxNumber] = useState("");
  const [orderCtxCurrency, setOrderCtxCurrency] = useState("EUR");
  useEffect(() => {
    if (!orderCtxId) return;
    (async () => {
      try {
        const o = await (await import("@/lib/masterOrderApi")).getOrder(orderCtxId);
        setOrderCtxNumber(displayOrderNumber(o?.order_number));
        if (o?.currency_code) setOrderCtxCurrency(o.currency_code);
      } catch { /* silent */ }
    })();
  }, [orderCtxId]);
  const [phase, setPhase] = useState("paste"); // paste | review | resize | result
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [recipe, setRecipe] = useState({
    title: "", category: "Другое", yield_quantity: null, yield_unit: null, items: [], technique: "",
  });
  const [fromShape, setFromShape] = useState(emptyShape());
  const [toShape, setToShape] = useState(emptyShape());
  const [toHeightTouched, setToHeightTouched] = useState(false);
  const [result, setResult] = useState(null);
  const [costOpen, setCostOpen] = useState(false);
  const [costItems, setCostItems] = useState([]);
  const [costSource, setCostSource] = useState("recipe");
  const [costSnapshot, setCostSnapshot] = useState(null);

  // Workbook — «Рабочая тетрадь» integration
  const [savedEntryId, setSavedEntryId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingMyRecipe, setSavingMyRecipe] = useState(false);
  const [savedMyRecipeId, setSavedMyRecipeId] = useState(null);
  const [customWeightFor, setCustomWeightFor] = useState(null);
  const [customWeight, setCustomWeight] = useState("");
  const [unresolvedEditFor, setUnresolvedEditFor] = useState(null);
  const [unresolvedDraft, setUnresolvedDraft] = useState({ name: "", amount: "", unit: "g" });
  const markDirty = () => { if (savedEntryId) setDirty(true); };

  // Picker: «Выбрать из моих рецептов» — reads existing workbook entries only,
  // never mutates them, never auto-links the loaded recipe to its source entry.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerEntries, setPickerEntries] = useState([]);
  const [pickerBusyId, setPickerBusyId] = useState(null);

  const openPicker = async () => {
    setPickerOpen(true);
    setPickerLoading(true);
    try {
      const rows = await listWorkbookEntries({ type: "recipe_calculation" });
      setPickerEntries(rows || []);
    } catch (e) {
      toast.error("Не удалось загрузить рецепты");
      setPickerEntries([]);
    } finally { setPickerLoading(false); }
  };

  // Display-side normalization ONLY at the Workbook → Recipe Recalc boundary.
  // The stored payload keeps full precision (Decimal-as-string). We round for
  // the review form so pastry chefs don't see 82.7605263157895 g after opening
  // a saved recalculated variant. Rules per user spec:
  //   • g, ml    → 1 fractional digit
  //   • kg, l    → 3 fractional digits (preserves 0.083 kg = 83 g fidelity)
  //   • pcs      → integer if effectively whole, else up to 2 digits
  //   • all rest → up to 2 fractional digits
  // Trailing zeros are stripped (82.0 → 82; 216.50 → 216.5) via parseFloat.
  // Anything unparseable falls through untouched.
  const AMOUNT_DIGITS_BY_UNIT = { g: 1, ml: 1, kg: 3, l: 3, pcs: 2, tbsp: 2, tsp: 2, cup: 2 };
  const normalizeAmountForReview = (raw, unit) => {
    if (raw === "" || raw === null || raw === undefined) return "";
    const n = Number(String(raw).replace(",", "."));
    if (!Number.isFinite(n)) return raw;
    const d = AMOUNT_DIGITS_BY_UNIT[unit] ?? 2;
    const rounded = parseFloat(n.toFixed(d));
    if (unit === "pcs" && Math.abs(rounded - Math.round(rounded)) < 1e-9) return Math.round(rounded);
    return rounded;
  };

  const normalizeIngredient = (row) => {
    const unit = row?.unit || "g";
    return {
      name: String(row?.name || "").trim(),
      amount: normalizeAmountForReview(row?.amount, unit),
      unit,
      note: row?.note || null,
      section: row?.section || null,
      raw_line: row?.raw_line || null,
    };
  };

  const pickEntry = async (id) => {
    if (pickerBusyId) return;
    setPickerBusyId(id);
    try {
      const entry = await getWorkbookEntry(id);
      const p = entry?.payload || {};
      // Prefer the recalculated variant if present, else the source recipe.
      const rawItems = (Array.isArray(p.recalculated_ingredients) && p.recalculated_ingredients.length > 0)
        ? p.recalculated_ingredients
        : (p.source_ingredients || []);
      const items = rawItems.map(normalizeIngredient).filter((r) => r.name);
      if (items.length === 0) {
        toast.error("В записи нет ингредиентов");
        setPickerBusyId(null);
        return;
      }
      // Reset the whole calc state so the flow starts fresh from "review".
      setRecipe({
        title: String(p.title || entry.title || "").trim(),
        category: categoryForRecipeSave(p.title || entry.title, p.category),
        yield_quantity: p.yield_quantity ?? null,
        yield_unit: p.yield_unit ?? null,
        items,
        technique: p.technique || "",
        components: p.components || [],
        unresolved_lines: p.unresolved_lines || [],
        original_text: p.original_text || null,
      });
      setFromShape(emptyShape());
      setToShape(emptyShape());
      setToHeightTouched(false);
      setResult(null);
      setCostSnapshot(null);
      // No auto-link: a new save (via existing action) creates a NEW entry.
      // Explicit user actions «Сохранить изменения» / «Сохранить как новый вариант»
      // remain the only ways to write to the workbook.
      setSavedEntryId(null);
      setDirty(false);
      setPickerOpen(false);
      setPhase("review");
    } catch (e) {
      toast.error("Не удалось открыть рецепт");
    } finally { setPickerBusyId(null); }
  };

  /* Height-mirroring: by default target height follows source height,
     so changing shape/size scales ingredients by area only.
     If user explicitly edits target height, they take over. */
  const handleFromShape = (next) => {
    const heightChanged = String(next.height ?? "") !== String(fromShape.height ?? "");
    setFromShape(next);
    if (heightChanged && !toHeightTouched) {
      setToShape((prev) => ({ ...prev, height: next.height }));
    }
  };
  const handleToShape = (next) => {
    if (String(next.height ?? "") !== String(toShape.height ?? "")) {
      setToHeightTouched(true);
    }
    setToShape(next);
  };

  const doParse = () => {
    if (!pasteText.trim()) { toast.error("Вставьте текст рецепта"); return; }
    if (pasteText.length > 5000) { toast.error("Слишком длинный текст (лимит 5000 символов)"); return; }
    const parsed = parseRecipe(pasteText);
    if (!hasRecognizedIngredients(parsed)) { toast.error("В тексте не найдено ни одной строки с ингредиентом и количеством"); return; }
    setRecipe({ ...parsed, category: suggestRecipeCategory(parsed.title) });
    setPasteOpen(false);
    setPhase("review");
  };

  const doConfirm = () => {
    const title = String(recipe.title || "").trim();
    if (!title) { toast.error("Укажите название рецепта"); return; }
    if (recipe.items.length === 0) { toast.error("Нужен хотя бы один ингредиент"); return; }
    if ((recipe.unresolved_lines || []).length > 0) {
      toast.error("Решите, что сделать с нераспознанными строками");
      return;
    }
    setRecipe((current) => ({ ...current, title }));
    // Confirm promotes the edited review rows to the current recipe state.
    // An older recalculation must never win over these user-confirmed values.
    setResult(null);
    setCostSnapshot(null);
    setPhase("choice");
  };

  const replaceReviewMeasure = (sourceIndex, amount) => {
    const sourceItem = recipe.items[sourceIndex];
    const resolvedItem = applyMetricResolution(sourceItem, amount);
    if (!resolvedItem) {
      toast.error("Введите вес больше нуля");
      return;
    }
    setRecipe((current) => ({
      ...current,
      items: current.items.map((item, index) =>
        index === sourceIndex ? applyMetricResolution(item, amount) : item
      ),
    }));
    setResult(null);
    setCostSnapshot(null);
    setCustomWeightFor(null);
    setCustomWeight("");
    markDirty();
  };

  const removeUnresolvedLine = (sourceIndex) => {
    setRecipe((current) => ({
      ...current,
      unresolved_lines: (current.unresolved_lines || []).filter((_, index) => index !== sourceIndex),
    }));
    setUnresolvedEditFor(null);
    markDirty();
  };

  const keepUnresolvedAsNote = (sourceIndex) => {
    setRecipe((current) => {
      const line = (current.unresolved_lines || [])[sourceIndex]?.trim();
      const technique = String(current.technique || "").trim();
      return {
        ...current,
        technique: line && !technique.includes(line) ? [technique, line].filter(Boolean).join("\n\n") : technique,
        unresolved_lines: (current.unresolved_lines || []).filter((_, index) => index !== sourceIndex),
      };
    });
    setUnresolvedEditFor(null);
    markDirty();
  };

  const fixUnresolvedLine = (sourceIndex) => {
    const amount = Number(String(unresolvedDraft.amount).replace(",", "."));
    const name = unresolvedDraft.name.trim();
    if (!name || !Number.isFinite(amount) || amount <= 0) {
      toast.error("Укажите ингредиент и количество больше нуля");
      return;
    }
    setRecipe((current) => ({
      ...current,
      items: [...current.items, {
        name,
        amount,
        unit: unresolvedDraft.unit,
        note: null,
        section: null,
        raw_line: (current.unresolved_lines || [])[sourceIndex] || null,
      }],
      unresolved_lines: (current.unresolved_lines || []).filter((_, index) => index !== sourceIndex),
    }));
    setUnresolvedEditFor(null);
    setUnresolvedDraft({ name: "", amount: "", unit: "g" });
    markDirty();
  };

  const openCostFromChoice = () => {
    setCostSource("recipe");
    setCostItems(recipe.items);
    setCostOpen(true);
  };
  const openCostFromResult = () => {
    setCostSource("result");
    setCostItems(result?.items || []);
    setCostOpen(true);
  };

  const applyInlineUnitCorrection = ({ sourceIndex, amount, unit }) => {
    const updateItems = (items) => (items || []).map((item, index) =>
      index === sourceIndex ? { ...item, amount, unit } : item);
    setCostSnapshot(null);
    setCostItems((current) => updateItems(current));
    // Confirmed recipe rows remain the persistent source for future cost,
    // Save as My Recipe and recalculation operations.
    setRecipe((current) => ({ ...current, items: updateItems(current.items) }));
    if (costSource === "result") {
      setResult((current) => {
        if (!current) return current;
        const items = updateItems(current.items);
        const totalGrams = items
          .filter((item) => item.unit === "g").reduce((sum, item) => sum + (Number(item.amount) || 0), 0)
          + items.filter((item) => item.unit === "kg").reduce((sum, item) => sum + (Number(item.amount) || 0) * 1000, 0);
        return { ...current, items, totalGrams };
      });
    }
    markDirty();
  };

  const factor = useMemo(() => {
    const a1 = shapeArea(fromShape); const a2 = shapeArea(toShape);
    if (!a1 || !a2) return null;
    const h1 = Number(fromShape.height) || 0;
    const h2 = Number(toShape.height) || 0;
    const useHeight = h1 > 0 && h2 > 0;
    const v1 = useHeight ? a1 * h1 * (Number(fromShape.count) || 1) : a1 * (Number(fromShape.count) || 1);
    const v2 = useHeight ? a2 * h2 * (Number(toShape.count) || 1) : a2 * (Number(toShape.count) || 1);
    return { value: v2 / v1, useHeight };
  }, [fromShape, toShape]);

  const doRecalc = () => {
    if (!factor || !Number.isFinite(factor.value) || factor.value <= 0) {
      toast.error("Проверьте размеры форм"); return;
    }
    const geometryCoefficient = factor.value;

    // ── Structural pcs renormalization for eggs ───────────────────────────
    // If ANY row of the confirmed recipe is «яйцо/яйца» in pcs, we compute an
    // integer working amount for eggs first, then derive a *working
    // coefficient* from egg ratio and apply it to every other ingredient so
    // proportions stay correct. Eggs given in grams stay in grams (never
    // enter this branch — the unit === "pcs" guard filters them out).
    const eggIdxs = [];
    let sourceEggsTotal = 0;
    recipe.items.forEach((it, idx) => {
      if (it.unit === "pcs" && isEggIngredient(it.name)) {
        eggIdxs.push(idx);
        sourceEggsTotal += Number(it.amount) || 0;
      }
    });

    let workingCoefficient = geometryCoefficient;
    let workingEggsTotal = null;
    let renormNote = null;
    if (eggIdxs.length > 0 && sourceEggsTotal > 0) {
      const exactEggsTotal = sourceEggsTotal * geometryCoefficient;
      workingEggsTotal = Math.max(1, Math.round(exactEggsTotal));
      // Guard against div-by-zero (unreachable given the > 0 check above).
      workingCoefficient = workingEggsTotal / sourceEggsTotal;
      renormNote = `Расчёт скорректирован под ${workingEggsTotal} ${workingEggsTotal === 1 ? "яйцо" : (workingEggsTotal < 5 ? "яйца" : "яиц")} для сохранения пропорций.`;
    }

    const newItems = recipe.items.map((it, idx) => {
      // Egg rows: distribute integer working total in proportion to their
      // source shares so a two-row egg recipe («желток 2 pcs», «яйцо 3 pcs»)
      // still sums up right. Single-row case = full total in that row.
      if (eggIdxs.includes(idx) && workingEggsTotal !== null) {
        const share = sourceEggsTotal > 0
          ? Math.max(1, Math.round((Number(it.amount) || 0) / sourceEggsTotal * workingEggsTotal))
          : workingEggsTotal;
        return { ...it, amount: share };
      }
      const exact = Number(it.amount) * workingCoefficient;
      const working = workingAmountFor(exact, it.name, it.unit);
      return { ...it, amount: working };
    });

    const totalGrams = newItems
      .filter((it) => it.unit === "g")
      .reduce((s, it) => s + Number(it.amount || 0), 0)
      + newItems.filter((it) => it.unit === "kg").reduce((s, it) => s + Number(it.amount || 0) * 1000, 0);
    trackMasterOrderTool("recipe_recalc", "recalc_recipe");
    setResult({
      items: newItems,
      factor: workingCoefficient,
      geometryCoefficient,
      useHeight: factor.useHeight,
      totalGrams,
      renormNote,
    });
    setPhase("result");
  };

  const copyResult = () => {
    if (!result) return;
    const lines = [
      `${recipe.title} — пересчитано ×${fmtNum(result.factor, 3)}`,
      `Форма: ${shapeSummary(toShape)}`,
      "",
      ...result.items.map((it) => `${it.name} — ${fmtNum(it.amount)} ${unitLabel(it.unit)}`),
    ].join("\n");
    navigator.clipboard?.writeText(lines);
    toast.success("Скопировано");
  };

  /* ────────────────────── Workbook «Рабочая тетрадь» ────────────────────── */
  const dec = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(String(v).replace(",", "."));
    return Number.isFinite(n) ? String(n) : null;
  };
  const shapeToPayload = (s) => {
    const out = {};
    if (s?.shape) out.shape = s.shape;
    for (const k of ["diameter", "side", "length", "width", "height", "count"]) {
      const v = dec(s?.[k]);
      if (v !== null) out[k] = v;
    }
    return Object.keys(out).length ? out : null;
  };
  const ingredientsToPayload = (arr) =>
    (arr || [])
      .filter((it) => (it?.name || "").trim())
      .map((it) => {
        const amount = dec(it.amount);
        const row = { name: it.name.trim() };
        if (amount !== null) row.amount = amount;
        if (it.unit) row.unit = it.unit;
        if (it.note) row.note = it.note;
        if (it.section) row.section = it.section;
        if (it.raw_line) row.raw_line = it.raw_line;
        return row;
      });

  const componentsToPayload = (items) => {
    const bySection = new Map();
    for (const item of items) {
      if (!item.section) continue;
      if (!bySection.has(item.section)) bySection.set(item.section, []);
      bySection.get(item.section).push(item);
    }
    return [...bySection.entries()].map(([name, ingredients]) => ({ name, ingredients }));
  };

  const buildWorkbookPayload = () => {
    // If the user is on the cost-only path (no recalculation happened),
    // do NOT persist mold geometry — target_shape would otherwise be a
    // synthetic snapshot of the empty default form.
    const hasResult = !!result;
    const sourceIngredients = ingredientsToPayload(recipe.items);
    return {
      title: (recipe.title || "Рецепт").trim(),
      category: categoryForRecipeSave(recipe.title, recipe.category),
      // Exact context for Shopping List. It is metadata only: Recipe Recalc
      // math, cost resolution and Workbook behavior remain unchanged.
      order_id: orderCtxId || null,
      order_item_id: orderItemCtxId || null,
      source_ingredients: sourceIngredients,
      components: componentsToPayload(sourceIngredients),
      unresolved_lines: recipe.unresolved_lines || [],
      original_text: recipe.original_text || null,
      technique: recipe.technique || null,
      recalculated_ingredients: hasResult ? ingredientsToPayload(result?.items || []) : [],
      source_shape: hasResult ? shapeToPayload(fromShape) : null,
      target_shape: hasResult ? shapeToPayload(toShape) : null,
      cost: costSnapshot || null,
      currency_code: costSnapshot?.currency_code || "EUR",
    };
  };

  const saveAsMyRecipe = async () => {
    // The result is authoritative only while the result screen is active.
    // Choice follows Confirm and therefore always saves recipe.items.
    const rows = (phase === "result" && result?.items?.length ? result.items : recipe.items)
      .filter((it) => (it?.name || "").trim() && Number(it.amount) > 0);
    if (!rows.length) {
      toast.error("Нужен хотя бы один ингредиент");
      return;
    }

    const unsupported = rows.filter((it) => !MY_RECIPE_BASE_UNIT[it.unit]);
    if (unsupported.length) {
      toast.error("Для сохранения замените неподдерживаемые единицы: " +
        [...new Set(unsupported.map((it) => unitLabel(it.unit)))].join(", "));
      return;
    }

    setSavingMyRecipe(true);
    try {
      const directory = await listIngredients();
      const byName = new Map(
        (directory || []).map((ing) => [String(ing.name || "").trim().toLocaleLowerCase("ru"), ing])
      );
      const ingredientLines = [];

      for (const row of rows) {
        const name = String(row.name || "").trim();
        const key = name.toLocaleLowerCase("ru");
        const requiredFamily = MY_RECIPE_UNIT_FAMILY[row.unit];
        let ingredient = byName.get(key);

        if (ingredient) {
          const existingFamily = MY_RECIPE_UNIT_FAMILY[ingredient.base_unit];
          if (existingFamily !== requiredFamily && !isEggMassCountPair(name, row.unit, ingredient.base_unit)) {
            throw new Error(`Ингредиент «${name}» уже существует с несовместимой единицей`);
          }
        } else {
          ingredient = await createIngredient({
            name,
            base_unit: MY_RECIPE_BASE_UNIT[row.unit],
            category: null,
            notes: null,
            is_active: true,
          });
          byName.set(key, ingredient);
        }

        ingredientLines.push({
          ingredient_id: ingredient.id,
          quantity: String(row.amount),
          unit: row.unit,
          notes: row.note?.trim() || null,
          section: row.section || null,
        });
      }

      const recognizedYield = Number(recipe.yield_quantity) > 0
        && MY_RECIPE_BASE_UNIT[recipe.yield_unit]
        ? {
            yield_quantity: String(recipe.yield_quantity),
            yield_unit: recipe.yield_unit,
          }
        : {};

      const saved = await createRecipe({
        name: String(recipe.title || "").trim(),
        category: categoryForRecipeSave(recipe.title, recipe.category),
        ...recognizedYield,
        scaling_method: "manual",
        notes: recipe.technique?.trim() || null,
        original_text: recipe.original_text || null,
        is_active: true,
        ingredient_lines: ingredientLines,
      });
      setSavedMyRecipeId(saved.id);
      trackMasterOrderTool("recipe_recalc", "save_my_recipe");
      toast.success("Сохранено в Мои рецептуры");
    } catch (e) {
      toast.error(e?.message || "Не удалось сохранить в Мои рецептуры");
    } finally {
      setSavingMyRecipe(false);
    }
  };

  const saveToWorkbook = async () => {
    setSaving(true);
    try {
      const created = await createWorkbookEntry({
        type: "recipe_calculation",
        payload: buildWorkbookPayload(),
      });
      setSavedEntryId(created.id);
      setDirty(false);
      trackMasterOrderTool("recipe_recalc", "workbook_save");
      toast.success("Сохранено в Рабочую тетрадь");
    } catch (e) {
      toast.error("Не удалось сохранить");
    } finally { setSaving(false); }
  };

  const saveChanges = async () => {
    if (!savedEntryId) return saveToWorkbook();
    setSaving(true);
    try {
      await updateWorkbookEntry(savedEntryId, { payload: buildWorkbookPayload() });
      setDirty(false);
      trackMasterOrderTool("recipe_recalc", "workbook_save");
      toast.success("Изменения сохранены");
    } catch (e) {
      toast.error("Не удалось сохранить изменения");
    } finally { setSaving(false); }
  };

  const saveAsVariant = async () => {
    if (!savedEntryId) return;
    setSaving(true);
    try {
      const variant = await duplicateWorkbookEntry(savedEntryId, {
        payload: buildWorkbookPayload(),
      });
      setSavedEntryId(variant.id);
      setDirty(false);
      trackMasterOrderTool("recipe_recalc", "workbook_save");
      toast.success("Сохранено как новый вариант");
    } catch (e) {
      toast.error("Не удалось сохранить вариант");
    } finally { setSaving(false); }
  };

  return (
    <MOShell active="recipe-recalc" title="Пересчитать рецепт" back="/master-order">
      <div className="max-w-3xl mx-auto px-4 md:px-6 pb-16 pt-4" data-testid="mo-recipe-recalc">
        {orderCtxId && (
          <div className="mb-4 flex items-center justify-between gap-2 rounded-lg px-3 py-2"
               style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
               data-testid="mo-recipe-recalc-order-context">
            <div className="text-[12.5px]" style={{ color: "#5B4326" }}>
              Открыто из заказа № {orderCtxNumber || "…"}
            </div>
            <button onClick={() => navigate(`/master-order/orders/${orderCtxId}`)}
                    className="h-8 px-3 rounded-xl text-[12px]"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-recipe-recalc-back-to-order">
              К заказу
            </button>
          </div>
        )}

        {/* ══════════ PHASE 1: PASTE ══════════ */}
        {phase === "paste" && (
          <div>
            <div className="text-center mb-6">
              <div className="text-[24px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                Новый расчёт
              </div>
              <div className="text-[13px] mt-1" style={{ color: "#8B6F4B" }}>
                Начните с рецепта — база ингредиентов не нужна.
              </div>
            </div>
            <div className="grid gap-2">
              <button onClick={() => setPasteOpen(true)}
                      className="rounded-xl bg-white p-4 text-left"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-recalc-paste-btn">
                <div className="text-[15px]" style={{ color: "#2A1D14" }}>Вставить рецепт</div>
                <div className="text-[12px] mt-1" style={{ color: "#8B6F4B" }}>Скопируйте текст из любого источника — до 5 000 символов.</div>
              </button>
              <button onClick={openPicker}
                      className="rounded-xl bg-white p-4 text-left"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-recalc-choose-btn">
                <div className="text-[15px]" style={{ color: "#2A1D14" }}>Выбрать из моих рецептов</div>
                <div className="text-[12px] mt-1" style={{ color: "#8B6F4B" }}>Открыть сохранённый рецепт из Рабочей тетради.</div>
              </button>
            </div>
          </div>
        )}

        {/* Compact paste modal */}
        {pasteOpen && (
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
               style={{ background: "rgba(35,23,16,0.5)" }}
               onClick={(e) => { if (e.target === e.currentTarget) setPasteOpen(false); }}
               data-testid="mo-recalc-paste-modal">
            <div className="w-full md:max-w-lg bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl flex flex-col"
                 style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)", maxHeight: "92dvh" }}>
              <div className="flex-shrink-0 p-5 md:p-6 pb-2">
                <div className="flex items-start justify-between mb-2">
                  <div className="text-[18px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Вставьте рецепт</div>
                  <button onClick={() => setPasteOpen(false)}
                          className="w-8 h-8 rounded-full grid place-items-center"
                          style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                          aria-label="Закрыть">
                    <X size={13} />
                  </button>
                </div>
                <div className="text-[12px]" style={{ color: "#8B6F4B" }}>
                  Вставь рецепт целиком — ингредиенты и приготовление можно оставить как в оригинале.
                </div>
              </div>
              <div className="flex-1 min-h-0 px-5 md:px-6 overflow-y-auto">
                <textarea value={pasteText}
                          onChange={(e) => setPasteText(e.target.value.slice(0, 5000))}
                          rows={8} placeholder="Название рецепта&#10;мука 250 г&#10;яйца 3 шт&#10;сахар 200 г"
                          className="w-full p-3 rounded-lg bg-white text-base md:text-[13px] outline-none resize-none"
                          style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14", minHeight: "160px" }}
                          data-testid="mo-recalc-paste-text" />
              </div>
              <div className="flex-shrink-0 flex items-center justify-between p-5 md:p-6 pt-3"
                   style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}>
                <div className="text-[11px]" style={{ color: "#B9A692" }}>{pasteText.length}/5000</div>
                <div className="flex gap-2">
                  <GhostBtn onClick={() => setPasteOpen(false)} testid="mo-recalc-paste-cancel">Отмена</GhostBtn>
                  <PrimaryBtn onClick={doParse} testid="mo-recalc-parse">Распознать рецепт</PrimaryBtn>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Picker modal: «Выбрать из моих рецептов» — read-only listing of
            existing Workbook entries. Selecting an entry loads it into the
            existing review flow WITHOUT creating a copy or linking to source. */}
        {pickerOpen && (
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
               style={{ background: "rgba(35,23,16,0.5)" }}
               onClick={(e) => { if (e.target === e.currentTarget) setPickerOpen(false); }}
               data-testid="mo-recalc-picker-modal">
            <div className="w-full md:max-w-lg bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl flex flex-col"
                 style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)", maxHeight: "92dvh" }}>
              <div className="flex-shrink-0 p-5 md:p-6 pb-2">
                <div className="flex items-start justify-between mb-2">
                  <div className="text-[18px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                    Выбрать из моих рецептов
                  </div>
                  <button onClick={() => setPickerOpen(false)}
                          className="w-8 h-8 rounded-full grid place-items-center"
                          style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                          aria-label="Закрыть"
                          data-testid="mo-recalc-picker-close">
                    <X size={13} />
                  </button>
                </div>
                <div className="text-[12px]" style={{ color: "#8B6F4B" }}>
                  Сохранённые в Рабочей тетради рецепты.
                </div>
              </div>
              <div className="flex-1 min-h-0 px-5 md:px-6 pb-4 overflow-y-auto">
                {pickerLoading && (
                  <div className="text-[13px] py-4" style={{ color: "#8B6F4B" }}>Загружаем…</div>
                )}
                {!pickerLoading && pickerEntries.length === 0 && (
                  <div className="rounded-lg p-3 text-center"
                       style={{ background: "#F7F1E7", color: "#5B4326",
                                boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                       data-testid="mo-recalc-picker-empty">
                    <div className="text-[13.5px]">В Рабочей тетради пока нет сохранённых рецептов</div>
                    <div className="text-[12px] mt-1" style={{ color: "#8B6F4B" }}>
                      Вставьте первый рецепт и сохраните его — он появится здесь.
                    </div>
                  </div>
                )}
                {!pickerLoading && pickerEntries.length > 0 && (
                  <div className="space-y-1.5" data-testid="mo-recalc-picker-list">
                    {pickerEntries.map((e) => (
                      <button key={e.id}
                              onClick={() => pickEntry(e.id)}
                              disabled={pickerBusyId === e.id}
                              className="w-full text-left rounded-lg p-3 bg-white disabled:opacity-60"
                              style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                              data-testid={`mo-recalc-picker-item-${e.id}`}>
                        <div className="text-[14px] truncate" style={{ color: "#2A1D14" }}>
                          {e.title || "Рецепт"}
                        </div>
                        <div className="text-[11.5px] mt-0.5" style={{ color: "#8B6F4B" }}>
                          Обновлено {fmtEntryDate(e.updated_at)}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}



        {/* ══════════ PHASE 2: REVIEW ══════════ */}
        {phase === "review" && (
          <div>
            <div className="text-[10.5px] tracking-[0.18em] uppercase mb-2" style={{ color: "#8B6F4B" }}>Проверьте рецепт</div>
            <Card testid="mo-recalc-review">
              <Label>Название</Label>
              <input value={recipe.title}
                     onChange={(e) => setRecipe({ ...recipe, title: e.target.value })}
                     placeholder="Укажите название рецепта"
                     className="w-full h-10 px-3 rounded-md bg-white text-base md:text-[13.5px] outline-none mb-3"
                     style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                     data-testid="mo-recalc-title" />
              <Label>Категория</Label>
              <select value={recipeCategoryOrFallback(recipe.category)}
                      onChange={(e) => setRecipe({ ...recipe, category: e.target.value })}
                      className="w-full h-10 px-3 rounded-md bg-white text-base md:text-[13px] outline-none mb-3"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-recalc-category">
                {RECIPE_CATEGORIES.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              {recipe.yield_quantity != null && recipe.yield_unit && (
                <div className="mb-3">
                  <Label>Выход</Label>
                  <div className="grid grid-cols-[minmax(0,1fr)_88px] gap-2">
                    <NumInput
                      value={recipe.yield_quantity}
                      onChange={(value) => setRecipe({ ...recipe, yield_quantity: value })}
                      testid="mo-recalc-yield-quantity"
                    />
                    <select
                      value={recipe.yield_unit}
                      onChange={(e) => setRecipe({ ...recipe, yield_unit: e.target.value })}
                      className="h-10 px-2 rounded-md bg-white text-base md:text-[13px] outline-none"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-recalc-yield-unit"
                    >
                      {["g", "kg", "ml", "l", "pcs"].map((unit) => (
                        <option key={unit} value={unit}>{unitLabel(unit)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              <Label>Ингредиенты</Label>
              <div className="space-y-1.5">
                {recipe.items.map((it, i) => {
                  const measure = getIngredientMeasureState(it);
                  const suggestion = measure.suggestion;
                  const displaySuggestion = suggestion
                    ? formatMeasureNumber(suggestion.metric_quantity)
                    : null;
                  const exactSuggestion = suggestion
                    ? formatMeasureNumber(suggestion.metric_quantity)
                    : null;
                  return (
                  <React.Fragment key={`${it.section || "ingredient"}-${i}`}>
                    {it.section && (i === 0 || recipe.items[i - 1]?.section !== it.section) && (
                      <div className="pt-2 pb-1 text-[11px] tracking-[0.16em] uppercase"
                           style={{ color: "#8B6F4B" }}
                           data-testid="mo-recalc-component-heading">
                        {it.section}
                      </div>
                    )}
                    <div className="grid grid-cols-[minmax(0,1fr)_64px_64px_26px] gap-1 items-center"
                         data-testid="mo-recalc-review-row">
                      <input value={it.name}
                             onChange={(e) => {
                               const val = e.target.value;
                               setRecipe((prev) => ({
                                 ...prev,
                                 items: prev.items.map((row, idx) => idx === i ? { ...row, name: val } : row),
                               }));
                             }}
                             className="h-9 px-2 rounded-md bg-white text-base md:text-[13px] outline-none min-w-0"
                             style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                      <input type="text" inputMode="decimal" value={it.amount ?? ""}
                             onChange={(e) => {
                               const rawAmount = String(e.target.value).replace(",", ".").trim();
                               const numericAmount = rawAmount === "" ? null : Number(rawAmount);
                               const parsed = numericAmount === null || Number.isFinite(numericAmount) ? numericAmount : null;
                               setRecipe((prev) => ({
                                 ...prev,
                                 items: prev.items.map((row, idx) => idx === i ? { ...row, amount: parsed } : row),
                               }));
                             }}
                             className="h-9 px-2 rounded-md bg-white text-base md:text-[13px] outline-none text-right tabular-nums min-w-0"
                             style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                      <select value={it.unit || ""}
                              onChange={(e) => {
                                const nextUnit = e.target.value;
                                setRecipe((prev) => ({
                                  ...prev,
                                  items: prev.items.map((row, idx) => idx === i ? { ...row, unit: nextUnit } : row),
                                }));
                              }}
                              className="h-9 px-1 rounded-md bg-white text-base md:text-[12px] outline-none min-w-0"
                              style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}>
                        <option value="">—</option>
                        {["g","kg","ml","l","tbsp","tsp","cup","pcs","pinch","handful","taste"].map((u) => (
                          <option key={u} value={u}>{unitLabel(u)}</option>
                        ))}
                      </select>
                      <button onClick={() =>
                                setRecipe((prev) => ({ ...prev, items: prev.items.filter((_, j) => j !== i) }))}
                              aria-label="Удалить"
                              className="w-[26px] h-[26px] rounded-full grid place-items-center justify-self-end"
                              style={{ background: "#F7F1E7", color: "#8B6F4B", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                        <X size={11} />
                      </button>
                      {ingredientReviewWarning(it) && (
                        <div className="col-span-4 text-[11px] px-2 pb-1"
                             style={{ color: "#A15C3A" }}
                             data-testid="mo-recalc-review-warning">
                          {ingredientReviewWarning(it)}
                        </div>
                      )}
                      {measure.state === MEASURE_STATE.APPROXIMATE && suggestion && (
                        <div className="col-span-4 rounded-lg px-3 py-2 mt-0.5"
                             style={{ background: "#FBF3E4", boxShadow: "inset 2px 0 0 0 #C7A574" }}
                             data-testid="mo-recalc-measure-suggestion">
                          <div className="text-[14px] tabular-nums" style={{ color: "#2A1D14" }}>
                            ≈ {displaySuggestion} г
                          </div>
                          <div className="text-[11px] mt-0.5" style={{ color: "#8B6F4B" }}>
                            Ориентировочное значение
                          </div>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            <button type="button"
                                    onClick={() => replaceReviewMeasure(i, suggestion.metric_quantity)}
                                    className="h-8 px-3 rounded-xl text-[12px]"
                                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                                    data-testid="mo-recalc-accept-measure">
                              Принять {exactSuggestion} г
                            </button>
                            <button type="button"
                                    onClick={() => { setCustomWeightFor(i); setCustomWeight(""); }}
                                    className="h-8 px-3 rounded-xl text-[12px]"
                                    style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                                    data-testid="mo-recalc-custom-weight-open">
                              Указать свой вес
                            </button>
                          </div>
                        </div>
                      )}
                      {measure.state === MEASURE_STATE.APPROXIMATE && !suggestion && (
                        <div className="col-span-4 rounded-lg px-3 py-2 mt-0.5"
                             style={{ background: "#FBF3E4", boxShadow: "inset 2px 0 0 0 #B26B4A" }}
                             data-testid="mo-recalc-measure-clarification">
                          <div className="text-[11.5px]" style={{ color: "#5B4326" }}>
                            Точный вес не указан. Для корректного расчёта укажи массу в граммах.
                          </div>
                          <button type="button"
                                  onClick={() => { setCustomWeightFor(i); setCustomWeight(""); }}
                                  className="h-8 px-3 rounded-xl text-[12px] mt-2"
                                  style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                                  data-testid="mo-recalc-custom-weight-open">
                            Указать вес
                          </button>
                        </div>
                      )}
                      {customWeightFor === i && (
                        <div className="col-span-4 grid grid-cols-[minmax(0,1fr)_auto_auto] gap-1.5 items-center px-2 pb-1"
                             data-testid="mo-recalc-custom-weight">
                          <input type="text" inputMode="decimal" value={customWeight}
                                 onChange={(event) => setCustomWeight(event.target.value)}
                                 placeholder="Вес в граммах"
                                 className="h-9 px-2 rounded-md bg-white text-base md:text-[13px] outline-none min-w-0"
                                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                                 data-testid="mo-recalc-custom-weight-input" />
                          <button type="button" onClick={() => replaceReviewMeasure(i, customWeight)}
                                  className="h-9 px-3 rounded-xl text-[12px]"
                                  style={{ background: "#4A3728", color: "#F7EFE0" }}
                                  data-testid="mo-recalc-custom-weight-apply">
                            Применить
                          </button>
                          <button type="button" onClick={() => { setCustomWeightFor(null); setCustomWeight(""); }}
                                  className="h-9 px-3 rounded-xl text-[12px]"
                                  style={{ color: "#8B6F4B" }}>
                            Отмена
                          </button>
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                  );
                })}
              </div>
              <button onClick={() => setRecipe({ ...recipe, items: [...recipe.items, { name: "", amount: 0, unit: "g", note: null, section: null }] })}
                      className="mt-2 h-8 px-3 rounded-xl text-[12px] inline-flex items-center gap-1"
                      style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-recalc-add-line">
                <Plus size={11} /> Добавить строку
              </button>

              {(recipe.unresolved_lines || []).length > 0 && (
                <div className="mt-4 pt-4 border-t border-[#EFE4D0]" data-testid="mo-recalc-unresolved-lines">
                  <Label>Нужно решить</Label>
                  <div className="text-[11.5px] mb-2" style={{ color: "#8B6F4B" }}>
                    Эти строки не были надёжно распознаны. Исправьте их, удалите из технологической карты или оставьте как текстовую заметку.
                  </div>
                  <div className="space-y-2">
                    {(recipe.unresolved_lines || []).map((line, unresolvedIndex) => (
                      <div key={`${line}-${unresolvedIndex}`} className="rounded-lg p-3"
                           style={{ background: "#FBF3E4", boxShadow: "inset 2px 0 0 0 #B26B4A" }}
                           data-testid="mo-recalc-unresolved-line">
                        <div className="text-[12.5px]" style={{ color: "#2A1D14" }}>{line}</div>
                        {unresolvedEditFor === unresolvedIndex ? (
                          <div className="mt-2 grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_80px_90px] gap-1.5">
                            <input value={unresolvedDraft.name}
                                   onChange={(event) => setUnresolvedDraft({ ...unresolvedDraft, name: event.target.value })}
                                   placeholder="Название ингредиента"
                                   className="h-9 px-2 rounded-md bg-white text-base md:text-[13px] outline-none min-w-0"
                                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                            <input value={unresolvedDraft.amount} inputMode="decimal"
                                   onChange={(event) => setUnresolvedDraft({ ...unresolvedDraft, amount: event.target.value })}
                                   placeholder="Кол-во"
                                   className="h-9 px-2 rounded-md bg-white text-base md:text-[13px] outline-none min-w-0"
                                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                            <select value={unresolvedDraft.unit}
                                    onChange={(event) => setUnresolvedDraft({ ...unresolvedDraft, unit: event.target.value })}
                                    className="h-9 px-2 rounded-md bg-white text-base md:text-[12px] outline-none"
                                    style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}>
                              {["g","kg","ml","l","tbsp","tsp","cup","pcs","pinch","handful","taste"].map((unit) => (
                                <option key={unit} value={unit}>{unitLabel(unit)}</option>
                              ))}
                            </select>
                            <div className="sm:col-span-3 flex flex-wrap gap-1.5">
                              <button type="button" onClick={() => fixUnresolvedLine(unresolvedIndex)}
                                      className="h-8 px-3 rounded-xl text-[12px]"
                                      style={{ background: "#4A3728", color: "#F7EFE0" }}>
                                Сохранить строку
                              </button>
                              <button type="button" onClick={() => setUnresolvedEditFor(null)}
                                      className="h-8 px-3 rounded-xl text-[12px]" style={{ color: "#8B6F4B" }}>
                                Отмена
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            <button type="button"
                                    onClick={() => {
                                      setUnresolvedEditFor(unresolvedIndex);
                                      setUnresolvedDraft({ name: "", amount: "", unit: "g" });
                                    }}
                                    className="h-8 px-3 rounded-xl text-[12px]"
                                    style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                              Исправить
                            </button>
                            <button type="button" onClick={() => removeUnresolvedLine(unresolvedIndex)}
                                    className="h-8 px-3 rounded-xl text-[12px]"
                                    style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                              Удалить из технологической карты
                            </button>
                            <button type="button" onClick={() => keepUnresolvedAsNote(unresolvedIndex)}
                                    className="h-8 px-3 rounded-xl text-[12px]"
                                    style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                              Оставить как текстовую заметку
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            <div className="mt-3">
              <Card testid="mo-recalc-technique">
                <Label>Технология приготовления</Label>
                <TechnologyReviewTextarea value={recipe.technique}
                                          onChange={(technology) => setRecipe({ ...recipe, technique: technology })} />
                <div className="mt-1 text-[11px]" style={{ color: "#B9A692" }}>
                  Не участвует в расчёте себестоимости — сохраняется отдельно.
                </div>
              </Card>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <GhostBtn onClick={() => setPhase("paste")} testid="mo-recalc-back-to-paste">Назад</GhostBtn>
              <PrimaryBtn onClick={doConfirm} testid="mo-recalc-confirm">Подтвердить</PrimaryBtn>
            </div>
          </div>
        )}

        {/* ══════════ PHASE 2.5: CHOICE ══════════ */}
        {phase === "choice" && (
          <div data-testid="mo-recalc-choice">
            <div className="text-[10.5px] tracking-[0.18em] uppercase mb-2" style={{ color: "#8B6F4B" }}>
              Что дальше
            </div>
            <div className="grid gap-2">
              <button onClick={() => setPhase("resize")}
                      className="rounded-xl bg-white p-4 text-left"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-recalc-choice-resize">
                <div className="text-[15px]" style={{ color: "#2A1D14" }}>Пересчитать форму</div>
                <div className="text-[12px] mt-1" style={{ color: "#8B6F4B" }}>
                  Изменить диаметр, высоту или количество форм — ингредиенты пересчитаются автоматически.
                </div>
              </button>
              <button onClick={openCostFromChoice}
                      className="rounded-xl bg-white p-4 text-left"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-recalc-choice-cost">
                <div className="text-[15px]" style={{ color: "#2A1D14" }}>Посчитать себестоимость</div>
                <div className="text-[12px] mt-1" style={{ color: "#8B6F4B" }}>
                  По исходным количествам рецепта — цены сохранятся и подтянутся в следующих расчётах.
                </div>
              </button>
              <button onClick={saveAsMyRecipe}
                      disabled={savingMyRecipe || !!savedMyRecipeId}
                      className="rounded-xl bg-white p-4 text-left disabled:opacity-60"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                      data-testid="mo-recalc-choice-save-my-recipe">
                <div className="text-[15px]" style={{ color: "#2A1D14" }}>Сохранить как мою рецептуру</div>
                <div className="text-[12px] mt-1" style={{ color: "#8B6F4B" }}>
                  Сохранить рецепт в «Мои рецептуры», чтобы использовать его в следующих заказах и расчётах.
                </div>
              </button>
            </div>
            <div className="mt-4 flex justify-end">
              <GhostBtn onClick={() => setPhase("review")} testid="mo-recalc-choice-back">Назад</GhostBtn>
            </div>
          </div>
        )}

        {/* ══════════ PHASE 3: RESIZE ══════════ */}
        {phase === "resize" && (
          <div>
            <div className="text-[10.5px] tracking-[0.18em] uppercase mb-2" style={{ color: "#8B6F4B" }}>Пересчитать форму</div>
            <div className="grid md:grid-cols-2 gap-3">
              <ShapeCard title="Форма в рецепте" value={fromShape} setValue={handleFromShape} testidPrefix="mo-recalc-from" />
              <ShapeCard title="Ваша форма" value={toShape} setValue={handleToShape} testidPrefix="mo-recalc-to" />
            </div>
            {factor && (
              <div className="mt-3 text-[12px]" style={{ color: "#8B6F4B" }} data-testid="mo-recalc-factor-preview">
                Коэффициент: ×{fmtNum(factor.value, 3)}
                {!factor.useHeight && " · без учёта высоты"}
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <GhostBtn onClick={() => setPhase("choice")} testid="mo-recalc-back-to-review">Назад</GhostBtn>
              <PrimaryBtn onClick={doRecalc} testid="mo-recalc-recalc">Пересчитать</PrimaryBtn>
            </div>
          </div>
        )}

        {/* ══════════ PHASE 4: RESULT ══════════ */}
        {phase === "result" && result && (
          <div>
            <div className="text-[10.5px] tracking-[0.18em] uppercase mb-2" style={{ color: "#8B6F4B" }}>Результат</div>
            <Card testid="mo-recalc-result">
              <div className="text-[18px] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                {recipe.title}
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4 text-[12.5px]">
                <div>
                  <Label>Было</Label>
                  <div style={{ color: "#2A1D14" }} data-testid="mo-recalc-result-from">{shapeSummary(fromShape)}</div>
                </div>
                <div>
                  <Label>Стало</Label>
                  <div style={{ color: "#2A1D14" }} data-testid="mo-recalc-result-to">{shapeSummary(toShape)}</div>
                </div>
              </div>
              <Label>Пересчитанные ингредиенты</Label>
              {result.renormNote && (
                <div className="text-[11.5px] mb-2 rounded-md px-2 py-1"
                     style={{ background: "#F7F1E7", color: "#5B4326",
                              boxShadow: "inset 2px 0 0 0 #B26B4A" }}
                     data-testid="mo-recalc-renorm-note">
                  {result.renormNote}
                </div>
              )}
              <div className="space-y-1" data-testid="mo-recalc-result-items">
                {result.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-[minmax(0,1fr)_72px_44px] gap-1.5 items-center py-0.5"
                       data-testid="mo-recalc-result-row">
                    <span className="truncate text-[13.5px] pr-1" style={{ color: "#2A1D14" }}>{it.name}</span>
                    <input type="text" inputMode="decimal" value={it.amount}
                           onChange={(e) => {
                             const raw = e.target.value;
                             const parsed = Number(String(raw).replace(",", "."));
                             const items = result.items.map((row, idx) =>
                               idx === i ? { ...row, amount: Number.isFinite(parsed) ? parsed : raw } : row);
                             // Recompute total on manual edits so the "per form" hint stays honest.
                             const totalGrams = items
                               .filter((r) => r.unit === "g").reduce((s, r) => s + (Number(r.amount) || 0), 0)
                               + items.filter((r) => r.unit === "kg").reduce((s, r) => s + (Number(r.amount) || 0) * 1000, 0);
                             setResult({ ...result, items, totalGrams });
                             setDirty(true);
                           }}
                           className="h-8 px-2 rounded-md bg-white text-base md:text-[13px] outline-none text-right tabular-nums min-w-0"
                           style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                           data-testid="mo-recalc-result-amount" />
                    <span className="text-[12px] text-left" style={{ color: "#5B4326" }}>{unitLabel(it.unit)}</span>
                  </div>
                ))}
              </div>
              {result.totalGrams > 0 && Number(toShape.count) > 1 && (
                <div className="mt-3 pt-3 border-t border-[#EFE4D0] text-[12.5px]" data-testid="mo-recalc-per-form">
                  <div style={{ color: "#5B4326" }}>
                    Общий вес теста: <b>{fmtNum(result.totalGrams)} г</b> · форм: {toShape.count} · на одну ≈ <b>{fmtNum(result.totalGrams / Number(toShape.count))} г</b>
                  </div>
                </div>
              )}
            </Card>

            <div className="mt-4 flex flex-wrap gap-2">
              <PrimaryBtn onClick={saveAsMyRecipe} disabled={savingMyRecipe || !!savedMyRecipeId}
                          testid="mo-recalc-save-my-recipe">
                {savingMyRecipe ? "Сохранение…" : (savedMyRecipeId ? "Сохранено в Мои рецептуры" : "Сохранить как мою рецептуру")}
              </PrimaryBtn>
              <PrimaryBtn onClick={openCostFromResult} testid="mo-recalc-cost">Посчитать себестоимость</PrimaryBtn>
              {!savedEntryId ? (
                <PrimaryBtn onClick={saveToWorkbook} disabled={saving} testid="mo-recalc-wb-save">
                  {saving ? "Сохранение…" : "Сохранить в Рабочую тетрадь"}
                </PrimaryBtn>
              ) : (
                <>
                  <PrimaryBtn onClick={saveChanges} disabled={saving} testid="mo-recalc-wb-save-changes">
                    {saving ? "Сохранение…" : "Сохранить изменения"}
                  </PrimaryBtn>
                  <GhostBtn onClick={saveAsVariant} testid="mo-recalc-wb-save-variant">
                    Сохранить как новый вариант
                  </GhostBtn>
                </>
              )}
              <GhostBtn onClick={() => toast("Скоро — добавление в заказ.")} testid="mo-recalc-add-to-order">Добавить в заказ</GhostBtn>
              <GhostBtn onClick={copyResult} testid="mo-recalc-copy"><Copy size={12} className="inline mr-1" />Скопировать</GhostBtn>
              <button onClick={() => setPhase("review")}
                      className="h-11 px-3 rounded-xl text-[12.5px]"
                      style={{ background: "transparent", color: "#8B6F4B" }}
                      data-testid="mo-recalc-edit">Редактировать</button>
            </div>
            <p className="mt-2 text-[11.5px]" style={{ color: "#8B6F4B" }}>
              Сохранённый материал останется доступен в Рабочей тетради после завершения тестирования.
            </p>
          </div>
        )}
      </div>

      <RecipeCostDrawer
        open={costOpen}
        onClose={() => setCostOpen(false)}
        recipeTitle={recipe.title}
        items={costItems}
        onItemsChange={applyInlineUnitCorrection}
        onSnapshot={setCostSnapshot}
        defaultCurrency={orderCtxCurrency}
        workbook={{
          savedEntryId,
          saving,
          onSave: saveToWorkbook,
          onSaveChanges: saveChanges,
          onSaveVariant: saveAsVariant,
        }}
      />
    </MOShell>
  );
}
